import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { playerApi } from '../../services/api';
import type { PaginatedResponse, Player } from '../../models';
import Paper from '@mui/material/Paper';
import { Link } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';


interface FilterState {
  role: string;
  is_international: string;
  gender: string;
  search: string;
  ordering: string;
}

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  role: yup.string().nullable(),
  batting: yup.string().nullable(),
  bowling: yup.string().nullable(),
  country: yup.number().nullable(),
});

const Players: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<FilterState>({
    role: '',
    is_international: '',
    gender: '',
    search: '',
    ordering: '',
  });
  const [searchInput, setSearchInput] = useState(filters.search);

  const roleOptions = [
    { value: '', label: 'All' },
    { value: 'BAT', label: 'Batsman' },
    { value: 'BOWL', label: 'Bowler' },
    { value: 'ALL', label: 'All-Rounder' },
    { value: 'WK', label: 'Wicket-Keeper' },
  ];

  const genderOptions = [
    { value: '', label: 'All' },
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'O', label: 'Other' },
  ];

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true, filterable: true, type: 'string' },
    { 
      field: 'image',
      headerName: 'Image',
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        params.value ? 
        <img 
          src={params.value} 
          alt={params.row.name} 
          style={{ width: '40px', height: '40px', borderRadius: '50%' }}
        /> : 
        <Box 
          sx={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            bgcolor: 'grey.300',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {params.row.name.charAt(0).toUpperCase()}
        </Box>
      )
    },
    { field: 'name', headerName: 'Name', width: 120, sortable: true, filterable: true },
    { field: 'country_name', headerName: 'Country (team)', width: 130, sortable: true, filterable: true },
    { field: 'nationality_name', headerName: 'Nationality', width: 130, sortable: false, filterable: false },
    { field: 'd_name', headerName: 'D Name', width: 120, sortable: true, filterable: true },
    { field: 'c_name', headerName: 'C Name', width: 120, sortable: true, filterable: true },
    { field: 'v_name', headerName: 'V Name', width: 120, sortable: true, filterable: true },
    // { field: 'f_name', headerName: 'F Name', width: 120, sortable: true, filterable: true },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 120, 
      sortable: true,
      filterable: true,
      type: 'singleSelect',
      valueOptions: roleOptions.map(opt => opt.value)
    },
    { field: 'batting', headerName: 'Batt', width: 70, sortable: true, filterable: true },
    { field: 'bowling', headerName: 'Bowl', width: 70, sortable: true, filterable: true },
    // { 
    //   field: 'd_verified', 
    //   headerName: 'D Ver', 
    //   width: 60, 
    //   type: 'boolean',
    //   sortable: true,
    //   filterable: true,
    //   renderCell: (params) => (
    //     <Box sx={{ color: params.value ? 'success.main' : 'error.main' }}>
    //       {params.value ? 'Yes' : 'No'}
    //     </Box>
    //   )
    // },
    { 
      field: 'is_international', 
      headerName: 'Int.', 
      width: 60, 
      type: 'boolean',
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <Box sx={{ color: params.value ? 'success.main' : 'error.main' }}>
          {params.value ? 'Yes' : 'No'}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box>
            <Tooltip title="View">
              <IconButton
                color="primary"
                size="small"
                component={Link}
                to={`/players/${params.row.id}/view`}
                sx={{ mr: 1 }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                color="info"
                size="small"
                onClick={() => handleEdit(params.row)}
                sx={{ mr: 1 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh Stats">
              <IconButton
                color="secondary"
                size="small"
                onClick={() => handleRefreshStats(params.row.id)}
                sx={{ mr: 1 }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                onClick={() => handleDelete(params.row.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const formik = useFormik<Player>({
    initialValues: {
      id: 0,
      name: '',
      d_name: null,
      c_name: null,
      mt_name: null,
      d_id: null,
      e_id: null,
      c_id: null,
      e_o_id: null,
      country: null,
      role: null,
      batting: null,
      bowling: null,
      d_verified: false,
      is_international: false,
      gender: null,
      notes: null,
      image: null,
      image_d: null,
      image_headshot: null
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selectedPlayer) {
          await playerApi.update(selectedPlayer.id, values);
        } else {
          await playerApi.create(values);
        }
        fetchPlayers(page);
        handleClose();
      } catch (error) {
        console.error('Error saving player:', error);
      }
    },
  });

  const fetchPlayers = async (pageNumber: number = 1, size: number = pageSize) => {
    try {
      const params: any = {
        page: pageNumber,
        page_size: size,
      };

      if (filters.role) {
        params.role = filters.role;
      }
      
      if (filters.is_international) {
        console.log('is_international', filters.is_international);
        params.is_international = filters.is_international === 'true';
      }

      if (filters.gender) {
        params.gender = filters.gender;
      }

      if (filters.search) {
        params.search = filters.search;
      }

      if (filters.ordering) {
        params.ordering = filters.ordering;
      }

      const response = await playerApi.getAll(params);
      const data = response.data as PaginatedResponse<Player>;
      setPlayers(data.results);
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortModelChange = (model: any) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      const ordering = sort === 'desc' ? `-${field}` : field;
      setFilters(prev => ({
        ...prev,
        ordering
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        ordering: ''
      }));
    }
  };

  const handleFilterModelChange = (model: any) => {
    const newFilters = { ...filters };
    
    if (model.items) {
      model.items.forEach((item: any) => {
        if (item.field === 'role') {
          newFilters.role = item.value;
        } else if (item.field === 'is_international') {
          newFilters.is_international = item.value;
        } else if (item.field === 'gender') {
          newFilters.gender = item.value;
        } else if (item.field === 'name' || item.field === 'd_name') {
          newFilters.search = item.value;
        }
      });
    }

    setFilters(newFilters);
  };

  useEffect(() => {
    fetchPlayers(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      if (filters.search !== searchInput) {
        setFilters((prev) => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      try {
        await playerApi.delete(id);
        fetchPlayers(page);
      } catch (error) {
        console.error('Error deleting player:', error);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPlayer(null);
    formik.resetForm();
  };

  const handleRefreshStats = async (playerId: number) => {
    try {
      await playerApi.refreshStats(playerId);
      alert('Stats refresh triggered successfully');
    } catch (error) {
      console.error('Error refreshing player stats:', error);
      alert('Failed to refresh player stats');
    }
  };

  const handleEdit = (player: Player) => {
    setSelectedPlayer(player);
    setOpen(true);
    formik.setValues(player);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" sx={{ fontSize: 22 }}>Players</Typography>
        <Button variant="contained" color="primary" size="small" onClick={() => setOpen(true)}>
          Add Player
        </Button>
      </Box>
      <Paper sx={{ p: 1, mb: 1 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Role"
              value={filters.role}
              onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}
            >
              {roleOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="International"
              value={filters.is_international}
              onChange={e => setFilters(f => ({ ...f, is_international: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Gender"
              value={filters.gender}
              onChange={e => setFilters(f => ({ ...f, gender: e.target.value }))}
            >
              {genderOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              fullWidth
              onClick={() => {
                setFilters({ role: '', is_international: '', gender: '', search: '', ordering: '' });
                setSearchInput('');
                setPage(1);
              }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <DataGrid
        rows={players}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: page - 1 },
          },
          columns: {
            columnVisibilityModel: {
              d_id: false,
              e_id: false,
              c_id: false,
              e_o_id: false
            },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        checkboxSelection
        disableRowSelectionOnClick
        loading={loading}
        rowCount={totalCount}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        density="compact"
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={handleFilterModelChange}
        onPaginationModelChange={(model) => {
          setPage(model.page + 1);
          setPageSize(model.pageSize);
        }}
      />
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 18 }}>
          {selectedPlayer ? `Edit Player: ${selectedPlayer.name}` : 'Add Player'}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="name"
                  label="Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="d_name"
                  label="Dream11 Name"
                  value={formik.values.d_name || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="c_name"
                  label="Circle Name"
                  value={formik.values.c_name || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="mt_name"
                  label="MyTeam11 Name"
                  value={formik.values.mt_name || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="role"
                  label="Role"
                  select
                  value={formik.values.role || ''}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">--Select Role--</MenuItem>
                  <MenuItem value="BAT">Batsman</MenuItem>
                  <MenuItem value="BOWL">Bowler</MenuItem>
                  <MenuItem value="ALL">All-Rounder</MenuItem>
                  <MenuItem value="WK">Wicket-Keeper</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="country"
                  label="Country ID"
                  type="number"
                  value={formik.values.country || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="batting"
                  label="Batting Style"
                  value={formik.values.batting || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="bowling"
                  label="Bowling Style"
                  value={formik.values.bowling || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="d_id"
                  label="Dream11 ID"
                  type="number"
                  value={formik.values.d_id || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="e_id"
                  label="ESPN ID"
                  type="number"
                  value={formik.values.e_id || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="c_id"
                  label="Circle ID"
                  type="number"
                  value={formik.values.c_id || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="gender"
                  label="Gender"
                  select
                  value={formik.values.gender || ''}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">--Select Gender--</MenuItem>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                  <MenuItem value="O">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="slug"
                  label="Slug"
                  value={formik.values.slug || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  margin="dense"
                  name="notes"
                  label="Notes"
                  multiline
                  rows={3}
                  value={formik.values.notes || ''}
                  onChange={formik.handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.d_verified || false}
                      onChange={formik.handleChange}
                      name="d_verified"
                    />
                  }
                  label="Dream11 Verified"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formik.values.is_international || false}
                      onChange={formik.handleChange}
                      name="is_international"
                    />
                  }
                  label="International Player"
                />
              </Grid>
              {selectedPlayer && selectedPlayer.image && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">Current Image</Typography>
                    <img 
                      src={selectedPlayer.image}
                      alt={selectedPlayer.name}
                      style={{ maxWidth: '100px', maxHeight: '100px' }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} size="small">Cancel</Button>
            <Button type="submit" variant="contained" color="primary" size="small">
              {selectedPlayer ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Players;